<div class="loading" *ngIf="loading"><mat-spinner diameter="28"></mat-spinner></div>
<div class="notice bad" *ngIf="error && !view">{{ error }}</div>

<div class="cv" *ngIf="view as v">
  <div class="main">
    <div class="head">
      <div>
        <h2>
          <app-person-link *ngIf="!v.conversation.simulated; else simName" [uid]="v.conversation.userId"
            [name]="memberLabel()" [big]="true"></app-person-link>
          <ng-template #simName>{{ memberLabel() }}</ng-template>
        </h2>
        <p class="sub">
          {{ v.cause?.label || v.conversation.cause }} · {{ stateLabels[v.conversation.state] || v.conversation.state }}
          · {{ v.conversation.botTurns }}/{{ v.conversation.maxBotTurns }} Sharon turns
        </p>
      </div>
      <div class="pills">
        <span class="pill" [attr.data-mode]="v.conversation.mode">{{ modeLabels[v.conversation.mode] }}</span>
        <span class="pill tag" *ngIf="v.conversation.simulated">Simulated</span>
        <span class="pill tag" *ngIf="v.conversation.isTest" title="A conversation with one of our own accounts, kept out of the counts">Test</span>
      </div>
    </div>

    <!-- Paused: the reason comes before anything else on the page. -->
    <div class="notice bad freeze" *ngIf="v.conversation.mode === 'paused'">
      <strong>Paused: {{ freezeLabel(v.conversation.freezeReason) }}</strong>
      <span *ngIf="lastPause as p">
        · {{ p.changedBy }}, {{ p.changedAt | date: 'd MMM, HH:mm' }}
        <span class="text" *ngIf="p.note"><br />{{ p.note }}</span>
      </span>
    </div>
    <div class="notice warn" *ngIf="v.conversation.mode === 'human'">
      Taken over. Sharon no longer writes here; reply from the team composer.
      <button mat-button *ngIf="!v.conversation.simulated" (click)="openComposer()">Open team composer</button>
    </div>
    <div class="notice info" *ngIf="v.conversation.mode === 'closed'">
      Closed{{ v.conversation.closeReason ? ': ' + v.conversation.closeReason : '' }}.
    </div>
    <div class="notice bad" *ngIf="error">{{ error }}</div>
    <div class="notice ok" *ngIf="notice">{{ notice }}</div>

    <!-- Mode control -->
    <section class="panel mode" *ngIf="v.conversation.mode !== 'closed'">
      <div class="row">
        <div class="seg" role="group" aria-label="Drive mode">
          <button type="button" [class.on]="v.conversation.mode === 'assisted'"
            [disabled]="!isOpenMode || v.conversation.mode === 'assisted' || busy" (click)="changeMode('assisted')">
            Assisted
          </button>
          <button type="button" [class.on]="v.conversation.mode === 'auto'"
            [disabled]="!isOpenMode || v.conversation.mode === 'auto' || busy" (click)="changeMode('auto')">
            Auto
          </button>
        </div>
        <button mat-stroked-button *ngIf="isOpenMode" (click)="changeMode('paused')" [disabled]="busy">Pause</button>
        <button mat-flat-button color="primary" *ngIf="v.conversation.mode === 'paused'"
          (click)="changeMode(v.conversation.resumeMode)" [disabled]="busy">
          Resume in {{ modeLabels[v.conversation.resumeMode] }}
        </button>
        <button mat-stroked-button *ngIf="v.conversation.mode !== 'human'" (click)="changeMode('human')"
          [disabled]="busy">Take over</button>
        <button mat-stroked-button class="danger" (click)="changeMode('closed')" [disabled]="busy">Close</button>
      </div>
      <p class="muted" *ngIf="pendingDraft && v.conversation.mode === 'assisted'">
        Switching to Auto leaves the pending draft pending: you decide it, then Sharon sends the turns after it.
      </p>
      <p class="muted" *ngIf="v.conversation.mode === 'paused'">
        While paused the mode toggle is off. Resume first.
      </p>
    </section>
    <details class="log" *ngIf="v.modeChanges.length">
      <summary>Mode changes ({{ v.modeChanges.length }})</summary>
      <ul>
        <li *ngFor="let m of v.modeChanges">
          {{ m.changedAt | date: 'd MMM, HH:mm' }} · {{ m.fromMode }} → {{ m.toMode }} · {{ m.changedBy }}
          <span class="text muted" *ngIf="m.note"> — {{ m.note }}</span>
        </li>
      </ul>
    </details>

    <!-- Thread -->
    <section class="thread">
      <p class="empty" *ngIf="!thread.length">Nothing said yet.</p>
      <ng-container *ngFor="let item of thread">
        <div class="msg" *ngIf="item.kind === 'turn'" [attr.data-role]="item.turn.role">
          <div class="who">
            {{ item.turn.role === 'bot' ? 'Sharon' : item.turn.role === 'member' ? memberLabel() : 'Team' }}
            <span class="muted">{{ item.turn.sentAt | date: 'd MMM, HH:mm' }}</span>
            <button type="button" class="chip" *ngIf="item.turn.brainOutput?.['edited'] === true"
              (click)="toggleEdit(item.turn.id)" [attr.aria-expanded]="openEdits.has(item.turn.id)">
              edited {{ openEdits.has(item.turn.id) ? '▴' : '▾' }}
            </button>
          </div>
          <div class="bubble text">{{ item.turn.content }}</div>
          <div class="original" *ngIf="openEdits.has(item.turn.id)">
            <ng-container *ngIf="item.draft as d; else noDraft">
              <div class="muted">Sharon's draft</div>
              <div class="text">{{ d.content }}</div>
              <div class="muted" *ngIf="d.editReason">Why it changed: <span class="text">{{ d.editReason }}</span></div>
            </ng-container>
            <ng-template #noDraft><span class="muted">The original draft is not in this view.</span></ng-template>
          </div>
          <app-proposal-card *ngFor="let p of learnedFor(item.draftId)" [proposal]="p" variant="learned"
            (changed)="onLearnedChanged($event)"></app-proposal-card>
        </div>

        <div class="msg ghost" *ngIf="item.kind === 'draft'" data-role="bot">
          <div class="who">
            <ng-container *ngIf="isBlocked(item.draft); else plainStatus">Blocked by checks</ng-container>
            <ng-template #plainStatus>Draft {{ item.draft.status }}</ng-template>
            <span class="muted">{{ item.at | date: 'd MMM, HH:mm' }}</span>
            <span class="muted" *ngIf="item.draft.decidedBy && !isBlocked(item.draft)">· {{ item.draft.decidedBy }}</span>
          </div>
          <div class="bubble text">{{ item.draft.content }}</div>
          <div class="muted text" *ngIf="item.draft.rejectReason">
            {{ isBlocked(item.draft) ? 'Checks' : 'Reason' }}: {{ item.draft.rejectReason }}
          </div>
          <app-proposal-card *ngFor="let p of learnedFor(item.draftId)" [proposal]="p" variant="learned"
            (changed)="onLearnedChanged($event)"></app-proposal-card>
        </div>
      </ng-container>
    </section>

    <!-- Approved, waiting for their time -->
    <div class="panel scheduled" *ngFor="let d of scheduledDrafts">
      <div class="muted">Scheduled for {{ (d.sendAt || d.proposedSendAt) | date: 'EEE d MMM, HH:mm' }}
        <span *ngIf="d.status === 'edited'">· edited</span></div>
      <div class="text">{{ d.finalContent || d.content }}</div>
    </div>

    <!-- The draft waiting for a decision -->
    <section class="panel draft" *ngIf="pendingDraft as d">
      <h3>Sharon's draft <span *ngIf="d.brainOutput?.['action'] === 'close'">(closing line)</span></h3>
      <textarea class="input" rows="5" [(ngModel)]="draftText" [disabled]="busy === 'draft'"
        aria-label="Draft text"></textarea>
      <div class="row" *ngIf="edited">
        <input class="input grow" [(ngModel)]="editReason" maxlength="500"
          placeholder="Why did you change it? (one line, optional)" aria-label="Why did you change it?" />
      </div>
      <p class="muted">Proposed send time: {{ d.proposedSendAt | date: 'EEE d MMM, HH:mm' }}</p>
      <ul class="problems" *ngIf="problems.length">
        <li *ngFor="let p of problems">{{ p }}</li>
      </ul>
      <div class="row">
        <button mat-flat-button color="primary" (click)="send('now')" [disabled]="busy || !draftText.trim()">
          Send now
        </button>
        <button mat-stroked-button (click)="send('proposed')" [disabled]="busy || !draftText.trim()">
          Send at proposed time
        </button>
        <button mat-stroked-button (click)="rejectOpen = !rejectOpen" [disabled]="busy">Reject…</button>
      </div>
      <div class="reject" *ngIf="rejectOpen">
        <div class="row">
          <input class="input grow" [(ngModel)]="rejectReason" maxlength="500"
            placeholder="Reason (optional, Sharon learns from it)" aria-label="Reject reason" />
          <label class="check"><input type="checkbox" [(ngModel)]="redraft" /> Draft again</label>
          <button mat-flat-button color="warn" (click)="reject()" [disabled]="busy">Reject draft</button>
        </div>
      </div>
    </section>

    <div class="row draft-now" *ngIf="canDraftNow">
      <button mat-stroked-button (click)="draftNow()" [disabled]="busy">
        {{ busy === 'draftNow' ? 'Sharon is writing…' : 'Draft now' }}
      </button>
      <span class="muted">Ask Sharon for the next message without waiting for pacing.</span>
    </div>
    <div class="notice info" *ngIf="lastOutcome as o">
      <ng-container [ngSwitch]="o.action">
        <span *ngSwitchCase="'wait'">Sharon suggests waiting<span *ngIf="o.rescheduledTo">, until
            {{ o.rescheduledTo | date: 'EEE d MMM, HH:mm' }}</span>.</span>
        <span *ngSwitchCase="'freeze'">Sharon froze the conversation: {{ freezeLabel(o.frozen?.reason ?? null) }}.</span>
        <span *ngSwitchCase="'close'">Sharon closed the conversation.</span>
        <span *ngSwitchDefault>Sharon: {{ o.action }}.</span>
      </ng-container>
      <span class="text" *ngIf="o.note"> {{ o.note }}</span>
    </div>
  </div>

  <!-- Who, why, and what we heard -->
  <aside class="side">
    <section class="panel">
      <h3>Member</h3>
      <div class="title">{{ memberLabel() }}</div>
      <div class="meta">{{ v.member?.city || 'City unknown' }} · tier {{ v.member?.tier || 'unknown' }}</div>
      <dl class="kv" *ngIf="personaLines().length">
        <ng-container *ngFor="let l of personaLines()">
          <dt>{{ l.key }}</dt>
          <dd>{{ l.value }}</dd>
        </ng-container>
      </dl>
    </section>
    <section class="panel" *ngIf="v.cause as cause">
      <h3>Cause</h3>
      <div class="title">{{ cause.label }}</div>
      <p class="meta">{{ cause.goal }}</p>
      <p class="meta">{{ v.causeTrusted ? 'Trusted for auto.' : 'Assisted only until trusted.' }}</p>
    </section>
    <section class="panel">
      <h3>Heard in this conversation</h3>
      <p class="meta" *ngIf="!v.intel.length">Nothing recorded yet.</p>
      <div class="intel" *ngFor="let i of v.intel">
        <span class="pill tag">{{ i.type }}</span> <span class="muted">{{ i.status }}</span>
        <div class="text">{{ i.summary }}</div>
        <div class="quote">“{{ i.quote }}”</div>
      </div>
    </section>
    <section class="panel">
      <h3>Ready for auto?</h3>
      <dl class="kv">
        <dt>Decided drafts (last 20)</dt><dd>{{ v.readiness.lastDecidedDrafts }}</dd>
        <dt>Unedited in a row</dt><dd>{{ v.readiness.unEditedInARow }} / 10</dd>
        <dt>Rejections, last 20</dt><dd>{{ v.readiness.rejectionsInLast20 }}</dd>
        <dt>Freezes, last 10 chats</dt><dd>{{ v.readiness.freezesInLast10Conversations }}</dd>
      </dl>
      <p class="meta">{{ v.readiness.ready ? 'All signals say this cause could run on auto.' : 'Not yet.' }}</p>
    </section>
  </aside>
</div>
